// Material preset registry.
//
// Wraps the built-in TSL materials in a uniform shape so the map driver can
// resolve `{preset: 'saturn', params: {...}}` entries from map.json without
// the driver knowing which shader it is. `paramsSchema` drives the editor's
// inspector panel and is applied (with fallbacks) at load time.
//
// Custom bundle-scoped materials live under bundles/<id>/materials/ and are
// loaded via a separate path (see MapArenaView).

import * as THREE from 'three';
import { CONFIG } from '../../config';
import { resolveUrl as resolveAssetUrl } from '../../ui/editor/EditorAssets';
import { AirshipSkyMaterial } from './AirshipSkyMaterial';
import { GridMaterial } from './GridMaterial';
import { LavaMaterial, PostMaterial } from './LavaMaterial';
import { MoonGroundMaterial } from './MoonGroundMaterial';
import { MoonSkyMaterial } from './MoonSkyMaterial';
import { SaturnSkyMaterial } from './SaturnSkyMaterial';
import { SeaSkyMaterial } from './SeaSkyMaterial';
import { SkyMaterial } from './SkyMaterial';
import { SpaceMaterial } from './SpaceMaterial';
import { WaterMaterial } from './WaterMaterial';
import type { ParamsSchema } from '../../maps/Behavior';
import { whenKtx2Ready, loadKtx2 } from '../textures/ktx2Loader';

export interface MaterialPresetEntry {
  /** Construct an instance of the material. `params` comes straight from
   *  map.json; the preset is responsible for parsing + applying it. */
  build: (params: Record<string, unknown>) => THREE.Material;
  /** Optional editor metadata. */
  paramsSchema?: ParamsSchema;
  /** Optional custom per-frame pump. If provided, replaces the default
   *  `material.setTime(t)` call — lets presets push extra per-frame uniforms
   *  (e.g. Saturn's debug-visibility toggle) in the same hook. */
  perFrame?: (material: THREE.Material, t: number) => void;
}

function num(params: Record<string, unknown>, key: string): number | undefined {
  const v = params[key];
  return typeof v === 'number' ? v : undefined;
}

export const MATERIAL_PRESETS: Record<string, MaterialPresetEntry> = {
  // --- Sky presets --------------------------------------------------------
  sandboxSky: {
    build: () => new SkyMaterial()
  },
  space: {
    build: () => new SpaceMaterial()
  },
  moonSky: {
    build: () => new MoonSkyMaterial()
  },
  saturn: {
    build: (_params) => {
      return new SaturnSkyMaterial();
    },
    paramsSchema: {
      orangeTint: { type: 'number', min: 0, max: 1, step: 0.01 }
    },
    perFrame: (material, t) => {
      const m = material as SaturnSkyMaterial;
      m.setTime(t);
      // Debug toggles — see-through-Saturn lets the ring/star layers behind
      // Saturn be inspected; orangeTint biases the gradient LUT toward a
      // saturated orange for art reviews.
      m.setSaturnVisibility(CONFIG.sim.debugSeeThroughSaturn ? 0 : 1);
      m.setOrangeTint(CONFIG.sim.saturnOrangeTint);
    }
  },
  airshipSky: {
    build: () => new AirshipSkyMaterial()
  },
  seaSky: {
    build: () => new SeaSkyMaterial()
  },

  // --- Ground / platform presets ------------------------------------------
  grid: {
    build: (params) => {
      const radius = num(params, 'radius') ?? 7.0;
      return new GridMaterial(radius);
    },
    paramsSchema: {
      radius: { type: 'number', min: 0.5, max: 30, step: 0.1 }
    }
  },
  lava: {
    build: () => new LavaMaterial()
  },
  // Procedural Gerstner-wave water. NOT used by the sinking-ship map
  // currently — that map ships with three's WaterMesh addon (see
  // bundles/sinking-ship/behaviors/oceanWater.ts). Kept registered so
  // a one-line swap in map.json (replace the oceanWater behavior with
  // a plane primitive using `{"preset": "water"}`) restores this path.
  water: {
    build: () => new WaterMaterial()
  },
  moonGround: {
    build: (params) => {
      // Load the moonsurface basecolor + heightmap. Albedo replaces the legacy
      // procedural palette; heightmap is sampled in the analytic shading path
      // for shading-only surface relief (no vertex displacement).
      const colorMap = loadKtx2('/materials/moonsurface/material_basecolor.ktx2');
      colorMap.colorSpace = THREE.SRGBColorSpace;
      colorMap.wrapS = colorMap.wrapT = THREE.RepeatWrapping;
      colorMap.anisotropy = 8;
      const heightMap = loadKtx2('/materials/moonsurface/material_height.ktx2');
      heightMap.colorSpace = THREE.NoColorSpace;
      heightMap.wrapS = heightMap.wrapT = THREE.RepeatWrapping;
      heightMap.anisotropy = 8;
      const mat = new MoonGroundMaterial({ colorMap, heightMap });
      // Tile scale: tiles per metre of world XZ. Default 0.05 = 1 tile per
      // 20 m. Pass `tileScale` in the map.json material.params block.
      const tileScale = num(params, 'tileScale');
      if (tileScale !== undefined) mat.uTileScale.value = tileScale;
      // Heightmap bump intensity. Stacks on top of the procedural shading.
      const heightBumpScale = num(params, 'heightBumpScale');
      if (heightBumpScale !== undefined) mat.uHeightBumpScale.value = heightBumpScale;
      return mat;
    },
    paramsSchema: {
      tileScale:        { type: 'number', min: 0.001, max: 5,  step: 0.001 },
      heightBumpScale:  { type: 'number', min: 0,     max: 10, step: 0.01  }
    }
  },
  post: {
    build: (params) => {
      const length = num(params, 'length') ?? 1.8;
      const colorHex = params.color as string | undefined;
      const color = colorHex !== undefined ? new THREE.Color(colorHex) : undefined;
      return new PostMaterial(length, color);
    },
    paramsSchema: {
      length: { type: 'number', min: 0.1, max: 10, step: 0.01 },
      color: { type: 'color' }
    }
  }
};

/** Sky presets are treated specially by the driver — they go on the inner
 *  face of a huge sphere and ignore depth. This helper exposes the set. */
export const SKY_PRESET_IDS = new Set(['sandboxSky', 'space', 'moonSky', 'saturn', 'airshipSky', 'seaSky']);

/** Build an inline PBR material from a map.json inline material block.
 *  Texture URLs (asset://… or absolute paths) are loaded asynchronously and
 *  applied to the returned material in place when each load resolves. */
export function buildInlinePBR(params: {
  color?: string; roughness?: number; metalness?: number;
  emissive?: string; emissiveIntensity?: number;
  mapColor?: string; mapNormal?: string; mapRoughness?: string;
  mapMetalness?: string; mapAo?: string; mapEmissive?: string;
  mapBump?: string;
  uvScale?: readonly [number, number];
  uvOffset?: readonly [number, number];
  normalScale?: number;
  bumpScale?: number;
  aoIntensity?: number;
}): THREE.Material {
  const mat = new THREE.MeshStandardMaterial({
    color: params.color !== undefined ? new THREE.Color(params.color) : 0xffffff,
    roughness: params.roughness ?? 1,
    metalness: params.metalness ?? 0,
    emissive: params.emissive !== undefined ? new THREE.Color(params.emissive) : 0x000000,
    emissiveIntensity: params.emissiveIntensity ?? 1,
    // bumpScale applies to the bumpMap (if/when it loads). Setting it on the
    // material now means the loader can drop the texture in lazily without
    // having to re-set the scale.
    bumpScale: params.bumpScale ?? 1
  });
  if (params.normalScale !== undefined) mat.normalScale.set(params.normalScale, params.normalScale);
  if (params.aoIntensity !== undefined) mat.aoMapIntensity = params.aoIntensity;

  const repeat = params.uvScale;
  const offset = params.uvOffset;
  const tuneTexture = (tex: THREE.Texture, srgb: boolean): void => {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    if (repeat) tex.repeat.set(repeat[0], repeat[1]);
    if (offset) tex.offset.set(offset[0], offset[1]);
    tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    tex.anisotropy = 8;
  };
  const lazyLoad = (url: string | undefined, srgb: boolean, apply: (t: THREE.Texture) => void): void => {
    if (!url) return;
    void whenKtx2Ready().then((loader) => {
      loader.load(resolveAssetUrl(url), (tex: THREE.Texture) => {
        tuneTexture(tex, srgb);
        apply(tex);
        mat.needsUpdate = true;
      }, undefined, (err: unknown) => {
        console.warn(`[buildInlinePBR] failed to load ${url}:`, err);
      });
    });
  };

  lazyLoad(params.mapColor,    true,  (t) => { mat.map = t; });
  lazyLoad(params.mapNormal,   false, (t) => { mat.normalMap = t; });
  lazyLoad(params.mapRoughness,false, (t) => { mat.roughnessMap = t; });
  lazyLoad(params.mapMetalness,false, (t) => { mat.metalnessMap = t; });
  lazyLoad(params.mapAo,       false, (t) => { mat.aoMap = t; });
  lazyLoad(params.mapEmissive, true,  (t) => { mat.emissiveMap = t; });
  lazyLoad(params.mapBump,     false, (t) => { mat.bumpMap = t; });

  return mat;
}

/** Returns [material, setTime?] — setTime may be undefined for presets that
 *  don't animate. The driver stores these and calls them each frame. A
 *  preset may override the default `material.setTime(t)` via its perFrame
 *  hook (e.g. saturn pumps two debug uniforms alongside time). */
export function buildPresetMaterial(
  preset: string,
  params: Record<string, unknown>
): { material: THREE.Material; setTime?: (t: number) => void } {
  const entry = MATERIAL_PRESETS[preset];
  if (!entry) {
    throw new Error(`[materials/presets] Unknown preset: "${preset}"`);
  }
  const material = entry.build(params);
  let setTime: ((t: number) => void) | undefined;
  if (entry.perFrame) {
    setTime = (t: number) => entry.perFrame!(material, t);
  } else {
    setTime = (material as { setTime?: (t: number) => void }).setTime?.bind(material);
  }
  return { material, setTime };
}
